// ============================================================================
// REVENUE CALCULATION MODAL - Transparent Methodology Display
// Shows step-by-step revenue at risk calculation like Insights
// ============================================================================

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { formatCurrency } from '../../utils/enhancedFormatters';

const RevenueCalculationModal = ({ company, isOpen, onClose }) => {
  if (!company) return null;

  // The calculation the backend actually ran: the same business-intelligence computation as the
  // client's own Compass (Pat, 2026-10-19). This modal used to show its own method — daily
  // revenue x recovery days from a tier table applied to the CRS — which is not how revenue at risk
  // is computed. Without the backend's figures there is nothing to show: "Not measured".
  const companyName = company.company_name || company.name || 'Unknown Company';
  const calc = company.revenue_at_risk_calculation || null;
  const revenueAtRisk = company.revenue_at_risk ?? null;
  const scored = calc !== null && revenueAtRisk !== null
    && [calc.annual_revenue, calc.risk_score, calc.base_risk_percentage, calc.enhanced_risk_percentage]
      .every((v) => typeof v === 'number');
  const pct = (v) => `${(v * 100).toFixed(1)}%`;

  // Calculation steps (only built when every input is present)
  const calculationSteps = !scored ? [] : [
    {
      step: 1,
      title: 'Annual Revenue',
      subtitle: 'Extracted from assessment data',
      value: formatCurrency(calc.annual_revenue),
      icon: '💰',
      color: 'bg-blue-50 border-blue-200',
      formula: 'From Assessment Question: Annual Company Revenue',
      note: 'Based on real assessment data, not estimates'
    },
    {
      step: 2,
      title: 'Overall Risk Score',
      subtitle: 'From the completed assessment',
      value: `${Math.round(calc.risk_score)}`,
      icon: '⚠️',
      color: 'bg-yellow-50 border-yellow-200',
      formula: 'Average of the Big 6 category scores (0–100, higher = more risk)',
      note: 'The same score the client sees in Compass'
    },
    {
      step: 3,
      title: 'Base Risk Percentage',
      subtitle: 'Share of revenue exposed at this risk score',
      value: pct(calc.base_risk_percentage),
      icon: '📐',
      color: 'bg-green-50 border-green-200',
      formula: `2% + (${Math.round(calc.risk_score)} ÷ 100 × 10%), capped at 12% = ${pct(calc.base_risk_percentage)}`,
      note: 'Rises with the risk score'
    },
    {
      step: 4,
      title: 'Adjusted Risk Percentage',
      subtitle: 'Adjusted for industry and company size',
      value: pct(calc.enhanced_risk_percentage),
      icon: '🏭',
      color: 'bg-purple-50 border-purple-200',
      formula: `${pct(calc.base_risk_percentage)} × ${calc.industry_risk_multiplier} (industry) × ${calc.size_risk_multiplier} (size) = ${pct(calc.enhanced_risk_percentage)}`,
      note: 'Industry and size multipliers from the Foundations risk model'
    },
    {
      step: 5,
      title: 'Revenue at Risk',
      subtitle: 'Total potential revenue loss',
      value: formatCurrency(revenueAtRisk),
      icon: '📉',
      color: 'bg-red-50 border-red-200',
      formula: `${formatCurrency(calc.annual_revenue)} × ${pct(calc.enhanced_risk_percentage)} = ${formatCurrency(revenueAtRisk)}`,
      note: 'Potential annual loss from a major risk incident'
    }
  ];

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] max-w-3xl w-full max-h-[90vh] overflow-y-auto p-0">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-[rgba(255,255,255,0.12)]">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <Dialog.Title className="text-xl font-bold text-[#e6f1ff]">Revenue at Risk Calculation</Dialog.Title>
              <p className="text-sm text-[#a8b2d1]">Transparent methodology for {companyName}</p>
            </div>
          </div>
          <Dialog.Close asChild>
            <button
              aria-label="Close"
              className="p-2 text-[#5a6a8a] hover:text-[#a8b2d1] hover:bg-[rgba(255,255,255,0.12)] rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </Dialog.Close>
        </div>

        {/* Final Result */}
        <div className="p-6 bg-blue-50 border-b border-[rgba(255,255,255,0.12)]">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#e6f1ff] mb-2">
              {scored ? formatCurrency(revenueAtRisk) : <span className="text-2xl text-[#8892b0]">Not measured</span>}
            </div>
            <div className="text-lg text-[#ccd6f6] mb-2">
              Potential revenue loss from a major risk incident
            </div>
            {scored ? (
              <div className="flex items-center justify-center space-x-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-green-700 font-medium">
                  Based on real assessment data, not estimates
                </span>
              </div>
            ) : (
              <div className="text-sm text-[#8892b0]">
                This client has no risk score yet — available once every persona has finished the assessment.
              </div>
            )}
          </div>
        </div>

        {/* Step-by-Step Calculation — only from a measured score */}
        <div className="p-6">
          {scored && <h3 className="text-lg font-semibold text-[#e6f1ff] mb-6">Step-by-Step Calculation</h3>}

          <div className="space-y-4">
            {scored && calculationSteps.map((step, index) => (
              <div key={index} className={`border rounded-lg p-4 ${step.color}`}>
                <div className="flex items-start space-x-4">
                  {/* Step Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 ra-glass rounded-full flex items-center justify-center text-lg">
                      {step.icon}
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-base font-semibold text-[#e6f1ff]">
                          {step.step}. {step.title}
                        </h4>
                        <p className="text-sm text-[#a8b2d1]">{step.subtitle}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-[#e6f1ff]">
                          {step.value}
                        </div>
                      </div>
                    </div>

                    {/* Formula */}
                    <div className="ra-glass bg-opacity-70 rounded p-3 mb-2">
                      <div className="text-sm font-medium text-[#ccd6f6] mb-1">Formula</div>
                      <div className="text-sm font-mono text-[#e6f1ff] bg-[#172a45] p-2 rounded">
                        {step.formula}
                      </div>
                    </div>

                    {/* Note */}
                    <div className="text-xs text-[#a8b2d1]">
                      {step.note}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Key Methodology Points */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-[#ccd6f6] mb-1">Key Methodology Points</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li className="flex items-start space-x-2">
                    <span className="w-1 h-1 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                    <span>Revenue data extracted directly from assessment responses (no hardcoded values)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1 h-1 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                    <span>Recovery time based on risk score severity (higher risk = longer recovery)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[rgba(255,255,255,0.03)] border-t border-[rgba(255,255,255,0.12)] rounded-b-lg">
          <div className="text-center">
            <Dialog.Close asChild>
              <button
                className="px-6 py-2 bg-[#172a45] text-white rounded-lg hover:bg-[#1e3148] transition-colors font-medium"
              >
                Close
              </button>
            </Dialog.Close>
          </div>
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RevenueCalculationModal;